import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import CardPayrollRevenueAndDeductTabUX from "./CardPayrollRevenueAndDeductTabUX";
import { Input, Button, Icon, Modal, Dropdown, FormField, Table, Radio } from "semantic-ui-react";
import { DateTextBox } from "react-lib/apps/common";
import { RunSequence, SetProp, BTN_ACTS, CARD_NAME } from "./sequence/Payroll";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

type CardPayrollRevenueAndDeductTabProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
  PayrollSequence: any;
  runSequence: RunSequence;
  masterOptions: any;
  languageUX?: any;
};

const statusMap: Record<string, string> = {
  ACTIVE: "Active",
  INACTIVE: "In Active",
};

const itemTypeMap: Record<string, string> = {
  REGULAR: "Regular",
  IRREGULAR: "Irregular",
};

const taxCalcMap: Record<string, string> = {
  CALCULATE_SECTION_40_1: "Total Income Calculation for Section 40(1)",
  CALCULATE_SECTION_40_2: "Total Income Calculation for Section 40(2)",
  EXCLUDE_THIS_ITEM: "Exclude this item",
};

const recordedAccountMap: Record<string, string> = {
  INCLUDE_IN_SALARY: "Include in salary",
  EXCLUDE_THIS_ITEM: "Exclude this item",
};

const CardPayrollRevenueAndDeductTab = (props: CardPayrollRevenueAndDeductTabProps) => {
  console.log("Payroll Revenue and Deduct Tab props:", props);

  useEffect(() => {
    props.runSequence({ sequence: "Payroll", restart: true });
  }, []);

  const getTabStyle = (targetType: "REVENUE" | "DEDUCT") => ({
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    padding: "10px 16px",
    fontWeight: "bold",
    backgroundColor: activeItemType === targetType ? "#2185d0" : undefined,
    color: activeItemType === targetType ? "white" : undefined,
    borderBottom: "none",
  });

  const [openAddModal, setOpenAddItemModal] = useState(false);

  const renderAddRevenueButton = () => (
    <Button
      id="CardPayrollRevenueAndDeductUX-Button-AddRevenue"
      color="green"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: "bold",
        marginBottom: "5px",
      }}
      onClick={() => {
        setActiveItemType("REVENUE");
        setSelectedItem(null);
        setOpenAddItemModal(true);
        resetItemFormFields();
      }}
    >
      <Icon name="plus" />
      Add New Revenue Item
    </Button>
  );

  const renderAddDeductButton = () => (
    <Button
      id="CardPayrollRevenueAndDeductUX-Button-AddDeduct"
      color="orange"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: "bold",
        marginBottom: "5px",
      }}
      onClick={() => {
        setActiveItemType("DEDUCT");
        setSelectedItem(null);
        setOpenAddItemModal(true);
        resetItemFormFields();
      }}
    >
      <Icon name="plus" />
      Add New Deduct Item
    </Button>
  );

  const [activeItemType, setActiveItemType] = useState<"REVENUE" | "DEDUCT">("REVENUE");
  const [itemId, setItemId] = useState<number | null>(null);
  const [itemCode, setItemCode] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [nameTh, setNameTh] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [itemType, setItemType] = useState<"REGULAR" | "IRREGULAR">("REGULAR");
  const [account, setAccount] = useState("");
  const [calculateAnnual, setCalculateAnnual] = useState<string>(
    activeItemType === "DEDUCT" ? "EXCLUDE_THIS_ITEM" : "CALCULATE_SECTION_40_1"
  );
  const [calculateSalary, setCalculateSalary] = useState<string>(
    activeItemType === "DEDUCT" ? "EXCLUDE_THIS_ITEM" : "INCLUDE_IN_SALARY"
  );
  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [effectiveStart, setEffectiveStart] = useState("");
  const [effectiveEnd, setEffectiveEnd] = useState("");
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [filterCode, setFilterCode] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const revenueItems = useMemo(
    () => props.PayrollSequence?.revenueItems ?? [],
    [props.PayrollSequence?.revenueItems]
  );
  const deductItems = useMemo(
    () => props.PayrollSequence?.deductItems ?? [],
    [props.PayrollSequence?.deductItems]
  );
  const revenueOrDeductdata = useMemo(() => {
    return activeItemType === "REVENUE" ? revenueItems : deductItems;
  }, [activeItemType, revenueItems, deductItems]);
  const [filteredData, setFilteredData] = useState<(Record<string, any> | { isEmpty: true })[]>([]);

  const formatToBackendDate = (thaiDate: string): string => {
    const parts = thaiDate.split("/");
    if (parts.length !== 3) {
      return "";
    }

    const [day, month, year] = parts;
    const gregorianYear = parseInt(year) - 543;

    return moment(`${gregorianYear}-${month}-${day}`, "YYYY-MM-DD").format("YYYY-MM-DD");
  };

  const formatToDisplayDate = (isoDate: string): string => {
    return moment(isoDate, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  const [selectedItemCode, setSelectedItemCode] = useState<string | null>(null);
  const [activePage, setActivePage] = React.useState(1);

  const itemsTable = () => {
    const rowsPerPage = 15;
    const data = filteredData ?? [];
    const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
    const startIdx = (activePage - 1) * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    const pageData = data.slice(startIdx, endIdx);
    const paddedData = [...pageData];
    while (paddedData.length < rowsPerPage) paddedData.push({ isEmpty: true });

    const headerStyle = {
      backgroundColor: "#add8d6",
      border: "1px solid #82c8e5",
    };

    return (
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={headerStyle}>Code</Table.HeaderCell>
              <Table.HeaderCell style={headerStyle}>Name (TH)</Table.HeaderCell>
              <Table.HeaderCell style={headerStyle}>Name (ENG)</Table.HeaderCell>
              <Table.HeaderCell style={headerStyle}>Type</Table.HeaderCell>
              <Table.HeaderCell style={headerStyle}>Account to be recorded</Table.HeaderCell>
              {activeItemType !== "DEDUCT" && (
                <Table.HeaderCell style={headerStyle}>Calculate annual revenue</Table.HeaderCell>
              )}
              <Table.HeaderCell style={headerStyle}>Effective Date</Table.HeaderCell>
              <Table.HeaderCell textAlign="center" style={headerStyle}>
                Status
              </Table.HeaderCell>
              <Table.HeaderCell style={headerStyle}></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paddedData.map((item: any, index: number) =>
              item.isEmpty ? (
                <Table.Row key={`empty-${index}`}>
                  {Array.from({ length: activeItemType === "REVENUE" ? 9 : 8 }).map((_, colIdx) => (
                    <Table.Cell key={colIdx}>&nbsp;</Table.Cell>
                  ))}
                </Table.Row>
              ) : (
                <Table.Row
                  key={index}
                  style={{
                    backgroundColor: item.code === selectedItemCode ? "#e6f7ff" : "transparent",
                    transition: "background-color 0.2s ease-in-out",
                    cursor: "default",
                  }}
                  onMouseEnter={(e: any) => {
                    if (item.code !== selectedItemCode) {
                      e.currentTarget.style.backgroundColor = "#eaf6ff";
                    }
                  }}
                  onMouseLeave={(e: any) => {
                    if (item.code !== selectedItemCode) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <Table.Cell>{item.code}</Table.Cell>
                  <Table.Cell>{item.name_th}</Table.Cell>
                  <Table.Cell>{item.name_en}</Table.Cell>
                  <Table.Cell>{itemTypeMap[item.item_type]}</Table.Cell>
                  <Table.Cell>{recordedAccountMap[item.account_to_be_record]}</Table.Cell>
                  {activeItemType !== "DEDUCT" && (
                    <Table.Cell>{taxCalcMap[item.calculate_total_tax_revenue]}</Table.Cell>
                  )}
                  <Table.Cell>
                    {item.effective_date_start && item.effective_date_end
                      ? `${formatToDisplayDate(item.effective_date_start)} - ${formatToDisplayDate(
                          item.effective_date_end
                        )}`
                      : ""}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {statusMap[item.status] === "Active" ? (
                      <Button icon color="green" size="mini" style={{ borderRadius: "50%" }}>
                        <Icon name="check" />
                      </Button>
                    ) : (
                      <Button icon color="red" size="mini" style={{ borderRadius: "50%" }}>
                        <Icon name="close" />
                      </Button>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Icon
                      name="edit"
                      color="orange"
                      size="big"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelectedItem(item);
                        handleEditItem(item);
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              )
            )}
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell
                colSpan={9}
                style={{ backgroundColor: "#f2f2f2", textAlign: "center" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Button
                    icon="chevron left"
                    size="mini"
                    disabled={activePage === 1}
                    onClick={() => setActivePage((prev) => Math.max(1, prev - 1))}
                  />
                  <span style={{ fontWeight: "bold" }}>
                    {activePage} / {totalPages || 1}
                  </span>
                  <Button
                    icon="chevron right"
                    size="mini"
                    disabled={activePage === totalPages || totalPages === 0}
                    onClick={() => setActivePage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    );
  };

  console.log("revenueItems:", revenueItems);
  console.log("deductItems:", deductItems);
  console.log("filteredData:", filteredData);

  const handleSearchRevenueAndDeductItems = async (buttonKey: string) => {
    if (!buttonKey || typeof buttonKey !== "string") {
      console.warn("Invalid buttonKey passed to handleSearchRevenueAndDeductItems:", buttonKey);
      return;
    }
    props.setProp(`buttonLoadCheck.${buttonKey}`, "LOADING");
    await new Promise((resolve) => setTimeout(resolve, 100));
    try {
      const currentData = activeItemType === "REVENUE" ? localRevenueItems : deductItems;

      const filtered = currentData.filter((item: any) => {
        const matchesCode =
          !filterCode || item.code?.toLowerCase().includes(filterCode.toLowerCase());
        const matchesName =
          !filterName ||
          item.name_th?.toLowerCase().includes(filterName.toLowerCase()) ||
          item.name_en?.toLowerCase().includes(filterName.toLowerCase());
        const matchesType = !filterType || itemTypeMap[item.item_type] === filterType;
        const matchesStatus = !filterStatus || statusMap[item.status] === filterStatus;

        return matchesCode && matchesName && matchesType && matchesStatus;
      });

      setFilteredData(filtered);
      props.setProp(`buttonLoadCheck.${buttonKey}`, "SUCCESS");
      setTimeout(() => {
        props.setProp(`buttonLoadCheck.${buttonKey}`, null);
      }, 1500);
    } catch (error) {
      props.setProp(`buttonLoadCheck.${buttonKey}`, "ERROR");
      props.setProp(`errorMessage.${buttonKey}`, "Failed to filter revenue/deduct items.");
      console.error("Error filtering items:", error);
    }
  };

  useEffect(() => {
    setFilteredData(activeItemType === "REVENUE" ? revenueItems : deductItems);
  }, [activeItemType, revenueItems, deductItems]);

  useEffect(() => {
    setFilterCode("");
    setFilterName("");
    setFilterType("");
    setFilterStatus("");
    setActivePage(1);
    setFilteredData(activeItemType === "REVENUE" ? revenueItems : deductItems);
  }, [activeItemType, revenueItems, deductItems]);

  const handleFilterCodeChange = (e: any, { value }: any) => {
    setFilterCode(value);
  };

  const handleFilterNameChange = (e: any, { value }: any) => {
    setFilterName(value);
  };

  const handleFilterTypeChange = (e: any, { value }: any) => {
    setFilterType(value);
  };

  const handleFilterStatusChange = (e: any, { value }: any) => {
    setFilterStatus(value);
  };

  const isEditMode = !!selectedItem;

  const formatNumber = (value: number | string | undefined | null): string => {
    const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;

    if (isNaN(num as number) || num == null) {
      return "-";
    }

    return (num as number).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const [filteredAndSearchedData, setFilteredAndSearchedData] = useState<any[]>([]);

  console.log("filtered data", filteredAndSearchedData);

  const revenueItemTypeOptions = [
    { key: "all", text: "All", value: "" },
    { key: "regular", text: "Regular", value: "Regular" },
    { key: "irregular", text: "Irregular", value: "Irregular" },
  ];

  const revenueItemStatusOptions = [
    { key: "all", text: "All", value: "" },
    { key: "active", text: "Active", value: "Active" },
    { key: "inactive", text: "In Active", value: "In Active" },
  ];

  const isItemFormValid = (): boolean => {
    const newErrors = {
      itemCode: itemCode.trim() === "",
      accountCode: accountCode.trim() === "",
      nameTh: nameTh.trim() === "",
      nameEn: nameEn.trim() === "",
      effectiveStart: effectiveStart.trim() === "",
      effectiveEnd: effectiveEnd.trim() === "",
    };
    setFieldErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const renderFieldError = (field: keyof typeof fieldErrors) => {
    if (field === "effectiveEnd" && customFieldErrors.effectiveEnd) {
      return (
        <div style={{ color: "red", fontSize: "15px", marginTop: "5px" }}>
          {customFieldErrors.effectiveEnd}
        </div>
      );
    }

    if (
      (field === "effectiveEnd" || field === "effectiveStart") &&
      (fieldErrors.effectiveStart || fieldErrors.effectiveEnd)
    ) {
      if (field === "effectiveEnd") {
        return (
          <div style={{ color: "red", fontSize: "15px", marginTop: "5px" }}>
            Please fill in this field
          </div>
        );
      }
      return null;
    }

    if (fieldErrors[field]) {
      return (
        <div style={{ color: "red", fontSize: "15px", marginTop: "5px" }}>
          Please fill in this field
        </div>
      );
    }

    return null;
  };

  const resetItemFormFields = () => {
    setItemId(null);
    setItemCode("");
    setAccountCode("");
    setNameTh("");
    setNameEn("");
    setItemType("REGULAR");
    setAccount("");
    setCalculateAnnual("");
    setCalculateSalary("");
    setStatus("ACTIVE");
    setEffectiveStart("");
    setEffectiveEnd("");
    setFieldErrors({
      itemCode: false,
      accountCode: false,
      nameTh: false,
      nameEn: false,
      effectiveStart: false,
      effectiveEnd: false,
    });
    setCustomFieldErrors({
      effectiveEnd: "",
    });
    setCodeError(null);
  };

  const [localRevenueItems, setLocalRevenueItems] = useState(revenueItems);
  const [localDeducteItems, setLocalDeductItems] = useState(deductItems);

  useEffect(() => {
    setLocalRevenueItems(revenueItems);
  }, [revenueItems]);

  const formatToThaiDate = (backendDate: string): string => {
    if (!backendDate) {
      return "";
    }

    const parts = backendDate.split("-");
    if (parts.length !== 3) {
      return "";
    }

    const [year, month, day] = parts;
    const buddhistYear = parseInt(year) + 543;

    return `${day}/${month}/${buddhistYear}`;
  };

  const [submittedActionKey, setSubmittedActionKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState({
    itemCode: false,
    accountCode: false,
    nameTh: false,
    nameEn: false,
    effectiveStart: false,
    effectiveEnd: false,
  });
  const [customFieldErrors, setCustomFieldErrors] = useState({
    effectiveEnd: "",
  });

  const parseDate = (dateStr: string) => {
    if (!dateStr) {
      return null;
    }
    const parts = dateStr.split("/");
    if (parts.length !== 3) {
      return null;
    }
    const [day, month, year] = parts.map((p) => parseInt(p, 10));
    if (!day || !month || !year || day < 1 || day > 31 || month < 1 || month > 12 || year < 1000) {
      return null;
    }
    return new Date(year, month - 1, day);
  };

  const validateDates = (startStr: string, endStr: string) => {
    setCustomFieldErrors({ effectiveEnd: "" });

    const startDate = parseDate(startStr);
    const endDate = parseDate(endStr);

    if (!startStr || !endStr) {
      setFieldErrors((prev) => ({
        ...prev,
        effectiveStart: !startStr,
        effectiveEnd: !startStr || !endStr,
      }));
      return;
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        effectiveStart: false,
        effectiveEnd: false,
      }));
    }

    if (startDate && endDate && endDate <= startDate) {
      setCustomFieldErrors({
        effectiveEnd: "Please specify end date greater than start date.",
      });
    }
  };

  const onEffectiveStartChange = (date: string) => {
    setEffectiveStart(date);
    validateDates(date, effectiveEnd);
  };

  const onEffectiveEndChange = (date: string) => {
    setEffectiveEnd(date);
    validateDates(effectiveStart, date);
  };

  const existingRevenueItemCodes = props.PayrollSequence?.revenueItems ?? [];
  const existingDeductItemCodes = props.PayrollSequence?.deductItems ?? [];

  const normalizedItemCode = itemCode.trim().toUpperCase();

  const isDuplicateItemCode =
    activeItemType === "REVENUE"
      ? existingRevenueItemCodes.some(
          (item: any) =>
            item.code?.trim().toUpperCase() === normalizedItemCode &&
            (!isEditMode || item.id !== itemId)
        )
      : existingDeductItemCodes.some(
          (item: any) =>
            item.code?.trim().toUpperCase() === normalizedItemCode &&
            (!isEditMode || item.id !== itemId)
        );

  const [lastSubmittedRevenue, setLastSubmittedRevenue] = useState<any>(null);
  const [lastSubmittedDeduct, setLastSubmittedDeduct] = useState<any>(null);

  const handleSubmitRevenueItem = (_e: any) => {
    if (!isItemFormValid()) {
      alert("Please fill in all required fields.");
      return;
    }

    const data = {
      id: itemId,
      code: itemCode,
      account_code: accountCode,
      name_th: nameTh,
      name_en: nameEn,
      item_type: itemType,
      account_to_be_record: account,
      calculate_total_tax_revenue: calculateAnnual,
      calculate_in_base_salary: calculateSalary,
      status: status,
      effective_date_start: formatToBackendDate(effectiveStart),
      effective_date_end: formatToBackendDate(effectiveEnd),
    };

    console.log(data);

    if (
      data.effective_date_start &&
      data.effective_date_end &&
      data.effective_date_end <= data.effective_date_start
    ) {
      alert("Please specify end date greater than start date.");
      return;
    }

    setLastSubmittedRevenue(data);

    if (itemId) {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_EDIT_REVENUE_ITEMS",
        item: data,
      });
      setSubmittedActionKey(BTN_ACTS.HANDLE_EDIT_REVENUE_ITEMS);
    } else {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_REVENUE_ITEMS",
        item: data,
      });
      setSubmittedActionKey(BTN_ACTS.HANDLE_SUBMIT_REVENUE_ITEMS);
    }
  };

  useEffect(() => {
    if (!submittedActionKey) {
      return;
    }

    const rawError = props.errorMessage?.[CARD_NAME];
    const status = props.buttonLoadCheck?.[submittedActionKey];

    const isRevenueSubmitOrEdit =
      submittedActionKey === BTN_ACTS.HANDLE_SUBMIT_REVENUE_ITEMS ||
      submittedActionKey === BTN_ACTS.HANDLE_EDIT_REVENUE_ITEMS;

    const isDeductSubmitOrEdit =
      submittedActionKey === BTN_ACTS.HANDLE_SUBMIT_DEDUCT_ITEMS ||
      submittedActionKey === BTN_ACTS.HANDLE_EDIT_DEDUCT_ITEMS;

    if ((isRevenueSubmitOrEdit || isDeductSubmitOrEdit) && rawError) {
      if (typeof rawError === "object" && rawError.code?.length > 0) {
        setCodeError(rawError.code.join(", "));
        alert(formatErrorMessage(rawError));
      }
      setSubmittedActionKey(null);
      return;
    }

    if (status === "SUCCESS") {
      if (isRevenueSubmitOrEdit && lastSubmittedRevenue) {
        if (submittedActionKey === BTN_ACTS.HANDLE_SUBMIT_REVENUE_ITEMS) {
          setLocalRevenueItems((prev: any) => [...prev, lastSubmittedRevenue]);
        } else {
          setLocalRevenueItems((prev: any) =>
            prev.map((i: any) => (i.id === lastSubmittedRevenue.id ? lastSubmittedRevenue : i))
          );
        }
        setLastSubmittedRevenue(null);
      }

      if (isDeductSubmitOrEdit && lastSubmittedDeduct) {
        if (submittedActionKey === BTN_ACTS.HANDLE_SUBMIT_DEDUCT_ITEMS) {
          setLocalDeductItems((prev: any) => [...prev, lastSubmittedDeduct]);
        } else {
          setLocalDeductItems((prev: any) =>
            prev.map((i: any) => (i.id === lastSubmittedDeduct.id ? lastSubmittedDeduct : i))
          );
        }
        setLastSubmittedDeduct(null);
      }

      setOpenAddItemModal(false);
      setSubmittedActionKey(null);
    }
  }, [props.errorMessage, props.buttonLoadCheck, submittedActionKey]);

  useEffect(() => {
    const fetchFreshData = async () => {
      await handleSearchRevenueAndDeductItems("SEARCH_REVENUE_DEDUCT_ITEMS");
    };

    fetchFreshData();
  }, []);

  const [showSnack, setShowSnack] = useState(true);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setShowSnack(true);
    }
  }, [props.errorMessage?.[CARD_NAME], props.successMessage?.[CARD_NAME]]);

  const handleSnackClose = (_event?: any, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setShowSnack(false);
    props.setProp(`errorMessage.${CARD_NAME}`, null);
    props.setProp(`successMessage.${CARD_NAME}`, null);
  };

  console.log("SUCCESS", props.successMessage?.[CARD_NAME]);

  const handleEditItem = (item: any) => {
    setItemId(item.id);
    setItemCode(item.code ?? "");
    setAccountCode(item.account_code ?? "");
    setNameTh(item.name_th ?? "");
    setNameEn(item.name_en ?? "");
    setItemType(item.item_type ?? "");
    setAccount(item.account_to_be_record ?? "");
    setCalculateAnnual(item.calculate_total_tax_revenue ?? "");
    setCalculateSalary(item.calculate_in_base_salary ?? "");
    setStatus(item.status ?? "");
    setEffectiveStart(formatToThaiDate(item.effective_date_start));
    setEffectiveEnd(formatToThaiDate(item.effective_date_end));

    setOpenAddItemModal(true);
  };

  const handleSubmitDeductItem = (_e: any) => {
    if (!isItemFormValid()) {
      alert("Please fill in all required fields.");
      return;
    }

    const data = {
      id: itemId,
      code: itemCode,
      account_code: accountCode,
      name_th: nameTh,
      name_en: nameEn,
      item_type: itemType,
      account_to_be_record: account,
      calculate_total_tax_revenue: calculateAnnual,
      calculate_in_base_salary: calculateSalary,
      status: status,
      effective_date_start: formatToBackendDate(effectiveStart),
      effective_date_end: formatToBackendDate(effectiveEnd),
    };

    console.log(data);

    if (
      data.effective_date_start &&
      data.effective_date_end &&
      data.effective_date_end <= data.effective_date_start
    ) {
      alert("Please specify end date greater than start date.");
      return;
    }

    setLastSubmittedDeduct(data);

    if (itemId) {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_EDIT_DEDUCT_ITEMS",
        item: data,
      });
      setSubmittedActionKey(BTN_ACTS.HANDLE_EDIT_DEDUCT_ITEMS);
    } else {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_DEDUCT_ITEMS",
        item: data,
      });
      setSubmittedActionKey(BTN_ACTS.HANDLE_SUBMIT_DEDUCT_ITEMS);
    }
  };

  const [codeError, setCodeError] = useState<string | null>(null);

  const formatErrorMessage = (errorObj: any): string => {
    if (typeof errorObj === "string") return errorObj;

    return Object.entries(errorObj)
      .map(([field, messages]) => {
        const msgText = Array.isArray(messages) ? messages.join(", ") : messages;
        return `${field}: ${msgText}`;
      })
      .join("\n");
  };

  useEffect(() => {
    if (!openAddModal) return;
    if (activeItemType === "DEDUCT") {
      setCalculateAnnual("EXCLUDE_THIS_ITEM");
      setCalculateSalary("EXCLUDE_THIS_ITEM");
    } else {
      setCalculateAnnual("");
      setCalculateSalary("");
    }
  }, [openAddModal, activeItemType]);

  return (
    <>
      <CardPayrollRevenueAndDeductTabUX
        languageUX={props.languageUX}
        renderAddRevenueButton={renderAddRevenueButton}
        renderAddDeductButton={renderAddDeductButton}
        itemsTable={itemsTable}
        activeItemType={activeItemType}
        onClickRevenueItem={() => setActiveItemType("REVENUE")}
        onClickDeductItem={() => setActiveItemType("DEDUCT")}
        getRevenueTabStyle={getTabStyle("REVENUE")}
        getDeductTabStyle={getTabStyle("DEDUCT")}
        revenueItemTypeOptions={revenueItemTypeOptions}
        revenueItemStatusOptions={revenueItemStatusOptions}
        filterCode={filterCode}
        filterName={filterName}
        filterType={filterType}
        filterStatus={filterStatus}
        handleFilterCodeChange={handleFilterCodeChange}
        handleFilterNameChange={handleFilterNameChange}
        handleFilterStatusChange={handleFilterStatusChange}
        handleFilterTypeChange={handleFilterTypeChange}
        handleSearchRevenueAndDeductItems={handleSearchRevenueAndDeductItems}
        handleTypeTextInDropdown={
          revenueItemTypeOptions.find((opt) => opt.value === filterType)?.text
        }
        handleStatusTextInDropdown={
          revenueItemTypeOptions.find((opt) => opt.value === filterStatus)?.text
        }
        renderSearchButton={
          <ButtonLoadCheck
            color="blue"
            onClick={() => handleSearchRevenueAndDeductItems("SEARCH_REVENUE_DEDUCT_ITEMS")}
            setProp={props.setProp}
            fluid={false}
            size="medium"
            paramKey="SEARCH_REVENUE_DEDUCT_ITEMS"
            buttonLoadCheck={props.buttonLoadCheck?.["SEARCH_REVENUE_DEDUCT_ITEMS"]}
            title="Search"
          />
        }
      />
      <div
        style={{
          filter: openAddModal ? "blur(4px)" : "none",
          transition: "filter 0.3s",
        }}
      >
        <Modal
          open={openAddModal}
          onClose={() => {
            setOpenAddItemModal(false);
            resetItemFormFields();
          }}
          size="large"
          dimmer={false}
          closeIcon
          style={{
            marginTop: showSnack ? "80px" : "auto",
          }}
        >
          <Modal.Header style={{ backgroundColor: "transparent", padding: "16px" }}>
            <div
              style={{
                backgroundColor: activeItemType === "REVENUE" ? "#21ba45" : "#f2711c",
                color: "white",
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: "4px",
                fontWeight: "bold",
              }}
            >
              {isEditMode
                ? activeItemType === "REVENUE"
                  ? "Edit Revenue Item"
                  : "Edit Deduct Item"
                : activeItemType === "REVENUE"
                ? "Add New Revenue Item"
                : "Add New Deduct Item"}
            </div>
          </Modal.Header>
          <Modal.Content>
            <div style={{ fontSize: "13px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", columnGap: "40px" }}>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        minWidth: "150px",
                        paddingBottom: "5px",
                        alignItems: "center",
                      }}
                    >
                      <label style={{ fontWeight: "bold" }}>Code</label>
                      <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                        *
                      </label>

                      {codeError && (
                        <span style={{ color: "red", marginLeft: "10px" }}>{codeError}</span>
                      )}
                    </div>
                    <Input
                      maxLength={20}
                      style={{ width: "400px" }}
                      value={itemCode}
                      onChange={(e) => setItemCode(e.target.value)}
                    />
                    <div style={{ marginLeft: "5px" }}>{renderFieldError("itemCode")}</div>
                  </div>
                </FormField>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>Account Code</label>
                      <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                        *
                      </label>
                    </div>
                    <Input
                      maxLength={20}
                      style={{ width: "400px" }}
                      value={accountCode}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^[0-9]*$/.test(val)) {
                          setAccountCode(val);
                        }
                      }}
                    />
                    <div style={{ marginLeft: "5px" }}>{renderFieldError("accountCode")}</div>
                  </div>
                </FormField>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: activeItemType === "REVENUE" ? "164px" : "146px",
                  rowGap: "16px",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <FormField>
                  <div
                    style={{
                      display: "flex",
                      minWidth: "150px",
                      alignItems: "center",
                      minHeight: "35px",
                    }}
                  >
                    <label style={{ fontWeight: "bold" }}>
                      {activeItemType === "REVENUE" ? "Rev. item type" : "Deduct item type"}
                    </label>
                    <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                      *
                    </label>
                    <Radio
                      label="Regular"
                      style={{ paddingRight: "10px" }}
                      checked={itemType === "REGULAR"}
                      onChange={() => setItemType("REGULAR")}
                    />
                    <Radio
                      label="Irregular"
                      style={{ paddingRight: "20px" }}
                      checked={itemType === "IRREGULAR"}
                      onChange={() => setItemType("IRREGULAR")}
                    />
                  </div>
                </FormField>
                <FormField>
                  <div
                    style={{
                      display: "flex",
                      minWidth: "150px",
                      alignItems: "center",
                      minHeight: "35px",
                    }}
                  >
                    <label style={{ fontWeight: "bold" }}>Effective Date</label>
                    <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                      *
                    </label>
                    <label style={{ fontWeight: "bold", paddingRight: "10px" }}>Start</label>
                    <DateTextBox
                      placeholder="dd/mm/yyyy"
                      value={effectiveStart}
                      onChange={onEffectiveStartChange}
                      inputStyle={{ width: "140px" }}
                    />
                    <label style={{ fontWeight: "bold", padding: "0 10px" }}>End</label>
                    <DateTextBox
                      placeholder="dd/mm/yyyy"
                      value={effectiveEnd}
                      onChange={onEffectiveEndChange}
                      inputStyle={{ width: "140px" }}
                    />
                    <div style={{ marginLeft: "5px", width: "120px" }}>
                      {renderFieldError("effectiveEnd")}
                    </div>
                  </div>
                </FormField>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", columnGap: "40px" }}>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>
                        {activeItemType === "REVENUE"
                          ? "Revenue item name in Thai"
                          : "Deduct item name in Thai"}
                      </label>
                      <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                        *
                      </label>
                    </div>
                    <Input
                      maxLength={500}
                      style={{ width: "400px" }}
                      value={nameTh}
                      onChange={(e) => setNameTh(e.target.value)}
                    />
                    <div style={{ marginLeft: "5px" }}>{renderFieldError("nameTh")}</div>
                  </div>
                </FormField>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>
                        {activeItemType === "REVENUE"
                          ? "Revenue item name in English"
                          : "Deduct item name in English"}
                      </label>
                      <label style={{ fontWeight: "bold", paddingRight: "5px", color: "red" }}>
                        *
                      </label>
                    </div>
                    <Input
                      maxLength={500}
                      style={{ width: "400px" }}
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                    />
                    <div style={{ marginLeft: "5px" }}>{renderFieldError("nameEn")}</div>
                  </div>
                </FormField>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", columnGap: "40px" }}>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>
                        {activeItemType === "REVENUE"
                          ? "Account code for this item"
                          : "Deduct code for this item"}
                      </label>
                    </div>
                    <Dropdown
                      selection
                      style={{ width: "400px" }}
                      options={[
                        { text: "Include in salary", value: "INCLUDE_IN_SALARY" },
                        { text: "Exclude this item", value: "EXCLUDE_THIS_ITEM" },
                      ]}
                      value={account}
                      onChange={(_, { value }) => setAccount(value as string)}
                    />
                  </div>
                </FormField>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>
                        Calculate in annual taxable revenue
                      </label>
                    </div>
                    <Dropdown
                      selection
                      style={{ width: "400px" }}
                      options={[
                        {
                          text: "Total Income Calculation for Section 40(1)",
                          value: "CALCULATE_SECTION_40_1",
                        },
                        {
                          text: "Total Income Calculation for Section 40(2)",
                          value: "CALCULATE_SECTION_40_2",
                        },
                        { text: "Exclude this item", value: "EXCLUDE_THIS_ITEM" },
                      ]}
                      value={calculateAnnual}
                      onChange={(_, { value }) => setCalculateAnnual(value as string)}
                    />
                  </div>
                </FormField>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", columnGap: "40px" }}>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>
                        Calculate in base salary for social security contribution
                      </label>
                    </div>
                    <Dropdown
                      selection
                      style={{ width: "400px" }}
                      options={[
                        { text: "Include in salary", value: "INCLUDE_IN_SALARY" },
                        { text: "Exclude this item", value: "EXCLUDE_THIS_ITEM" },
                      ]}
                      value={calculateSalary}
                      onChange={(_, { value }) => setCalculateSalary(value as string)}
                    />
                  </div>
                </FormField>
                <FormField>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", minWidth: "150px", paddingBottom: "5px" }}>
                      <label style={{ fontWeight: "bold" }}>Status</label>
                    </div>
                    <Dropdown
                      selection
                      style={{ width: "400px" }}
                      options={[
                        { text: "Active", value: "ACTIVE" },
                        { text: "In Active", value: "INACTIVE" },
                      ]}
                      value={status}
                      onChange={(_, { value }) => setStatus(value as "ACTIVE" | "INACTIVE")}
                    />
                  </div>
                </FormField>
              </div>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <ButtonLoadCheck
              color={isEditMode ? "orange" : "green"}
              onClick={
                activeItemType === "REVENUE" ? handleSubmitRevenueItem : handleSubmitDeductItem
              }
              setProp={props.setProp}
              fluid={false}
              size="medium"
              paramKey={
                activeItemType === "REVENUE"
                  ? isEditMode
                    ? BTN_ACTS.HANDLE_EDIT_REVENUE_ITEMS
                    : BTN_ACTS.HANDLE_SUBMIT_REVENUE_ITEMS
                  : isEditMode
                  ? BTN_ACTS.HANDLE_EDIT_DEDUCT_ITEMS
                  : BTN_ACTS.HANDLE_SUBMIT_DEDUCT_ITEMS
              }
              buttonLoadCheck={
                activeItemType === "REVENUE"
                  ? props.buttonLoadCheck?.[
                      isEditMode
                        ? BTN_ACTS.HANDLE_EDIT_REVENUE_ITEMS
                        : BTN_ACTS.HANDLE_SUBMIT_REVENUE_ITEMS
                    ]
                  : props.buttonLoadCheck?.[
                      isEditMode
                        ? BTN_ACTS.HANDLE_EDIT_DEDUCT_ITEMS
                        : BTN_ACTS.HANDLE_SUBMIT_DEDUCT_ITEMS
                    ]
              }
              title={isEditMode ? "Save and Edit" : "Save"}
            />
            <Button
              onClick={() => {
                setOpenAddItemModal(false);
                resetItemFormFields();
                setSubmittedActionKey(null);
              }}
            >
              Close
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
      {showSnack && (
        <SnackMessage
          onEvent={props.onEvent}
          onClose={handleSnackClose}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
        />
      )}
    </>
  );
};

export default React.memo(CardPayrollRevenueAndDeductTab);
